import { useAuth0 } from '@auth0/auth0-react'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import Logo from '../components/Common/Logo'
import LoginModal from '../components/Common/LoginModal'

export default function Home() {
  const { isAuthenticated } = useAuth0()
  const [showLogin, setShowLogin] = useState(false)

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-700 via-primary-600 to-primary-400 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-warm-400/20 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-8">
            <Logo size={120} />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">
            PetHalo
          </h1>
          <div className="w-16 h-0.5 bg-warm-400 mx-auto mb-4" />
          <p className="text-2xl md:text-3xl font-light mb-3 text-primary-100">
            Safe Pets. Smarter Tags.
          </p>
          <p className="text-lg mb-10 text-primary-200 max-w-2xl mx-auto">
            Smart NFC pet tags that help reunite lost pets with their families.
            Anyone can scan — no app needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isAuthenticated ? (
              <>
                <button
                  onClick={() => setShowLogin(true)}
                  className="bg-white text-primary-600 hover:bg-primary-50 font-semibold text-lg px-10 py-3.5 rounded-lg shadow-lg transition-colors"
                >
                  Get Started
                </button>
                <a
                  href="#how-it-works"
                  className="btn-outline text-lg px-10 py-3.5"
                >
                  Learn More
                </a>
              </>
            ) : (
              <Link
                to="/dashboard"
                className="bg-white text-primary-600 hover:bg-primary-50 font-semibold text-lg px-10 py-3.5 rounded-lg shadow-lg inline-block transition-colors"
              >
                Go to Dashboard
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center gap-6">
              {/* Tag Front */}
              <div className="w-40 h-40 rounded-full bg-primary-400 shadow-xl flex flex-col items-center justify-center border-4 border-primary-200">
                <Logo size={60} />
                <span className="text-white font-bold text-sm mt-1">PetHalo</span>
              </div>
              {/* Tag Back */}
              <div className="w-40 h-40 rounded-full bg-warm-50 shadow-xl flex flex-col items-center justify-center border-4 border-warm-200">
                <svg viewBox="0 0 24 24" className="w-16 h-16 text-primary-400" fill="none" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12 20.5h.01" />
                </svg>
                <span className="text-xs font-semibold text-gray-700 mt-1">TAP TO SCAN</span>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Smart NFC Pet Tags
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Durable, waterproof tags with NFC technology.
                Link to your pet's profile for a complete identification solution.
              </p>
              <ul className="space-y-3">
                {[
                  'NFC tap — instant access from any smartphone',
                  'Real-time push notifications when scanned',
                  'Location sharing to find your pet faster',
                  'Privacy controls — you decide what\'s visible',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">
            How PetHalo Works
          </h2>
          <p className="text-center text-gray-500 mb-14 max-w-2xl mx-auto">
            Three simple steps to protect your pet
          </p>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                step: '1',
                title: 'Get Your Tag',
                description: 'Order a PetHalo smart tag and attach it to your pet\'s collar.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                  </svg>
                ),
              },
              {
                step: '2',
                title: 'Register Your Pet',
                description: 'Create a profile with your pet\'s info and emergency contacts.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                ),
              },
              {
                step: '3',
                title: 'Get Instant Alerts',
                description: 'When someone scans your pet\'s tag, you\'re notified immediately with their location.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="relative mx-auto mb-6">
                  <div className="bg-primary-400 rounded-full w-20 h-20 flex items-center justify-center mx-auto text-white">
                    {item.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 bg-primary-700 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow">
                    {item.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gradient-to-br from-primary-700 to-primary-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-14">
            Why PetHalo?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Instant Scan',
                description: 'Anyone can scan the tag with their phone — no app needed.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
                  </svg>
                ),
              },
              {
                title: 'Call & Text',
                description: 'Finders can contact you instantly. You get real-time alerts.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                ),
              },
              {
                title: 'Location Sharing',
                description: 'Finders share their location so you know exactly where your pet is.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                ),
              },
              {
                title: 'Privacy First',
                description: 'You control exactly what info is visible. Your data stays safe.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.title} className="text-center p-6 rounded-xl bg-white/10 hover:bg-white/15 transition-colors">
                <div className="bg-white/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-primary-100 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-warm-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-3 text-gray-900">
            Protect Your Pet Today
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Easy to scan. Privacy protected. Peace of mind guaranteed.
          </p>
          {!isAuthenticated && (
            <button
              onClick={() => setShowLogin(true)}
              className="bg-primary-500 hover:bg-primary-600 text-white font-semibold text-lg px-10 py-3.5 rounded-lg transition-colors shadow-lg"
            >
              Sign Up Free
            </button>
          )}
        </div>
      </section>

      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </div>
  )
}
